import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';

import ConfirmDialog from 'components/confirm-dialog';
import { useCart } from 'hooks/use-cart';
import { t } from 'state/intl/translations';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import noop from 'utils/noop';

interface IDialogCallback {
  onConfirm?: Function;
  onCancel?: Function;
}

interface IUseSwitchCartMethodDialog {
  SwitchCartMethodDialog: FC;
  openSwitchCartMethod: (callbacks?: IDialogCallback) => void;
  shouldShowSwitchToStoreDialog: boolean;
  shouldShowSwitchToMobileDialog: boolean;
}

const useSwitchCartMethodDialog = (): IUseSwitchCartMethodDialog => {
  const {
    resetInRestaurantRedemption,
    inRestaurantRedemptionCart,
    inRestaurantRedemptionEnabled,
    enableOfferRedemption,
    enableRewardRedemption,
  } = useInRestaurantRedemptionContext();
  const { emptyCart, isCartEmpty } = useCart();
  const { cartEntries: minimalCartEntries, emptyMinimalCart } = useMinimalCart();

  const isInRestaurantRedemptionEmpty = !inRestaurantRedemptionCart?.length;

  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  const dialogCallbackRef = useRef<
    { onConfirm?: Function; onCancel?: Function } | null | undefined
  >(null);
  // Set when the user confirms; read in `onAfterClose` so the post-confirm work runs only on a
  // confirmed dismissal, never on cancel. Mirrors `pendingSwapRef` in the swap-dialog flow.
  const pendingConfirmRef = useRef(false);
  const dialogCopyRef = useRef({
    heading: t('changeRedemptionToInStore'),
    body: t('changeRedemptionToInStoreDescription'),
    confirmLabel: t('changeRedemptionToInStoreConfirmLabel'),
  });

  // Confirm only flags the pending confirmation and closes the dialog. The heavy work runs in
  // `onAfterClose` (below), once the dialog's exit animation has fully completed.
  const onConfirmSwitchCartMethodDialog = useCallback(() => {
    pendingConfirmRef.current = true;
    setOpen(false);
  }, []);

  // Runs after the dialog's exit animation completes and the native Modal is fully dismissed
  // (Dialog.onAfterClose). Gating the post-confirm work on the real close event — instead of a
  // fixed navigation buffer — keeps the destination screen from opening while the dialog is still
  // animating closed, so on iOS the two animations never overlap (BKPE-27150). Navigation runs
  // synchronously so React batches it with the consumer's redemption dispatches into a single
  // commit, unmounting this screen before the cart-reset runs (BKPE-27193). The cart-reset is
  // then deferred one frame so its render cascade lands behind the already-committed navigation
  // instead of pinning the JS thread ahead of it.
  const onAfterCloseSwitchCartMethodDialog = useCallback(() => {
    if (!pendingConfirmRef.current) {
      return;
    }
    pendingConfirmRef.current = false;
    const onConfirm = dialogCallbackRef.current?.onConfirm;
    dialogCallbackRef.current = null;

    if (typeof onConfirm === 'function') {
      onConfirm();
    }

    requestAnimationFrame(() => {
      if (!isInRestaurantRedemptionEmpty) {
        resetInRestaurantRedemption();
      } else if (!isCartEmpty) {
        emptyCart();
      } else if (minimalCartEntries.length > 0) {
        emptyMinimalCart();
      }
    });
  }, [
    isInRestaurantRedemptionEmpty,
    isCartEmpty,
    minimalCartEntries.length,
    resetInRestaurantRedemption,
    emptyCart,
    emptyMinimalCart,
  ]);

  const onCancelSwitchCartMethodDialog = useCallback(() => {
    pendingConfirmRef.current = false;
    if (typeof dialogCallbackRef.current?.onCancel === 'function') {
      dialogCallbackRef.current.onCancel();
    }

    dialogCallbackRef.current = null;
    setOpen(false);
  }, []);

  // should allow switching store if there is item in cart and brand enables offer or reward redemption
  const shouldShowSwitchToStoreDialog = Boolean(
    inRestaurantRedemptionEnabled &&
      (!isCartEmpty || minimalCartEntries.length > 0) &&
      (enableOfferRedemption || enableRewardRedemption)
  );
  const shouldShowSwitchToMobileDialog = Boolean(
    inRestaurantRedemptionEnabled && !isInRestaurantRedemptionEmpty
  );

  const confirmFnRef = useRef(onConfirmSwitchCartMethodDialog);
  confirmFnRef.current = onConfirmSwitchCartMethodDialog;
  const cancelFnRef = useRef(onCancelSwitchCartMethodDialog);
  cancelFnRef.current = onCancelSwitchCartMethodDialog;
  const afterCloseFnRef = useRef(onAfterCloseSwitchCartMethodDialog);
  afterCloseFnRef.current = onAfterCloseSwitchCartMethodDialog;

  const switchCartMethodDialogRef = useRef<FC | null>(null);
  if (!switchCartMethodDialogRef.current) {
    switchCartMethodDialogRef.current = function SwitchCartMethodDialog() {
      const dialogCopy = dialogCopyRef.current;

      return (
        <ConfirmDialog
          showDialog={openRef.current}
          heading={dialogCopy.heading}
          body={dialogCopy.body}
          confirmLabel={dialogCopy.confirmLabel}
          cancelLabel={t('noThankYou')}
          onConfirm={() => confirmFnRef.current()}
          onCancel={() => cancelFnRef.current()}
          onAfterClose={() => afterCloseFnRef.current()}
          onDismiss={noop}
          modalAppearanceEventMessage="Switching redemption method"
        />
      );
    };
  }
  const SwitchCartMethodDialog = switchCartMethodDialogRef.current;

  const openSwitchCartMethod = useCallback(
    (callbacks?: IDialogCallback) => {
      dialogCallbackRef.current = callbacks;
      dialogCopyRef.current = isInRestaurantRedemptionEmpty
        ? {
            heading: t('changeRedemptionToInStore'),
            body: t('changeRedemptionToInStoreDescription'),
            confirmLabel: t('changeRedemptionToInStoreConfirmLabel'),
          }
        : {
            heading: t('changeRedemptionToPickUp'),
            body: t('changeRedemptionToPickUpDescription'),
            confirmLabel: t('changeRedemptionToPickUpConfirmLabel'),
          };
      setOpen(true);
    },
    [isInRestaurantRedemptionEmpty]
  );

  return useMemo(
    () => ({
      SwitchCartMethodDialog,
      openSwitchCartMethod,
      shouldShowSwitchToStoreDialog,
      shouldShowSwitchToMobileDialog,
    }),
    [
      SwitchCartMethodDialog,
      openSwitchCartMethod,
      shouldShowSwitchToStoreDialog,
      shouldShowSwitchToMobileDialog,
    ]
  );
};

export default useSwitchCartMethodDialog;
